import React, { PureComponent } from 'react'
import { Col, Row, Button } from 'react-bootstrap';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, LabelProps, Label } from 'recharts';
import ReactDayPickerInput from 'react-day-picker/DayPickerInput';
//import Multiselect from 'react-multi-select-component';

import Navigation from '../../components/navigation/Navigation';
import { ErrorMessage, Loading } from '../../components/visuals/Visuals';
import Badge from '../../components/Badge';
import Multiselect from '../../components/Multiselect';

import { Prognosis } from '../../lib/utilities';
import { months_long_se, months_short_se, weekdays_long_se, weekdays_short_se } from '../../lib/constants';
import DataAccess from '../../lib/dataAccess/DataAccess';

import { yearGradeOptions } from '../../lib/constants';

interface Props {
    role: string
}

interface State {
    error: string | null,
    isLoading: boolean,
    filtersModified: boolean,
    //options
    courseOptions: { value:string, label:string }[],
    classOptions: { value:number, label:string, schoolId:number }[],
    schoolOptions: { value:number, label:string }[],
    teacherOptions: { value:number, label:string }[],
    yearGradeOptions: { value:number, label:string }[],
    programOptions: { value:string, label:string }[],
    genderOptions: { value:string, label:string }[],
    //filters
    startDate?: Date,
    endDate?: Date,
    courseIds: { value:string, label:string }[],
    classIds: { value:number, label:string }[],
    schoolIds: { value:number, label:string }[],
    genders: { value:string, label:string }[],
    teachers: { value:number, label:string }[],
    yearGrades: { value:number, label:string }[],
    programs: { value:string, label:string }[],

    data: any[]
}

export default class FractionalStatisticsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.getFilterOptions = this.getFilterOptions.bind(this);
        this.fetchData = this.fetchData.bind(this);

        this.removeCourseIdFilter = this.removeCourseIdFilter.bind(this);
        this.removeClassIdFilter = this.removeClassIdFilter.bind(this);
        this.removeSchoolIdFilter = this.removeSchoolIdFilter.bind(this);
        this.removeGenderFilter = this.removeGenderFilter.bind(this);
        this.removeYearGradeFilter = this.removeYearGradeFilter.bind(this);
        this.removeProgramFilter = this.removeProgramFilter.bind(this);
        this.removeTeacherFilter = this.removeTeacherFilter.bind(this);

        this.state = {
            error: null,
            isLoading: true,
            filtersModified: false,
            //options
            courseOptions: [],
            classOptions: [],
            schoolOptions: [],
            yearGradeOptions: yearGradeOptions,
            programOptions: [],
            teacherOptions: [],
            genderOptions: [{value:'K', label:'Flicka'}, {value:'M', label: 'Pojke'}],
            //filters
            startDate: undefined,
            endDate: undefined,
            courseIds: [],
            classIds: [],
            schoolIds: [],
            genders: [],
            yearGrades: [],
            programs: [],
            teachers: [],

            data: []
        }
    }

    componentDidMount() {
        this.getFilterOptions();
    }

    async getFilterOptions() {
        this.setState({ isLoading: true, error: null });
        //Hämta klasser
        //Hämta Program
        //Hämta Kurser
        //Hämta Ämne
        //Kön
        //Hämta Lärare

        try {
            let coursesPromise = DataAccess.General.getAllCourses();
            let classesPromise = DataAccess.General.getAllClasses();
            //Get programs
            let schoolPromise = DataAccess.General.getAllSchools();
            let teacherPromise = DataAccess.General.getTeachers();
            let programPromise = DataAccess.General.getAllPrograms();
    
            let courses = await coursesPromise;
            let classes = await classesPromise;
            let schools = await schoolPromise;
            let teachers = await teacherPromise;
            let programs = await programPromise;

            const courseObject = courses.reduce((acc: any, course) => {
                acc[course.name] = course;
                return acc;
            }, {});
            const uniqueCourses = Object.keys(courseObject).map(key => courseObject[key]);
            
            const programObject = [{id: -1, code: 'GR', name: 'Grundskola'}].concat(programs).reduce((acc: any, program) => {
                acc[program.code] = program;
                return acc;
            }, {});
            const uniquePrograms = Object.keys(programObject).map(key => programObject[key]).filter(p => p.name != "undefined");

            let courseOptions = uniqueCourses.map(c => ({value: c.name, label: c.displayName}));
            let classOptions = classes.filter(c => c.school != null)
                                .map(c => ({value: c.id, label: `${c.name}, ${c.school?.name}`, schoolId: (c.school?.id || -1)}));
            let schoolOptions = schools.map(s => ({value: s.id, label: s.name}));
            let teacherOptions = teachers.map(t => ({value: t.id, label: `${t.fullname}, ${t.externalId}`}));
            let programOptions = uniquePrograms.map(p => ({value: p.code, label: p.name}));

            this.setState({ error: null, isLoading: false, courseOptions, classOptions, schoolOptions, teacherOptions, programOptions });
        }
        catch(e) {
            this.setState({ error: e, isLoading: false });
        }
    }

    removeCourseIdFilter(index: number) {
        this.setState(({courseIds}) => ({ courseIds: [...courseIds.slice(0, index), ...courseIds.slice(index + 1)], filtersModified: true }));
    }

    removeClassIdFilter(index: number) {
        this.setState(({classIds}) => ({ classIds: [...classIds.slice(0, index), ...classIds.slice(index + 1)], filtersModified: true }));
    }

    removeSchoolIdFilter(index: number) {
        this.setState(({schoolIds}) => ({ schoolIds: [...schoolIds.slice(0, index), ...schoolIds.slice(index + 1)], filtersModified: true }));
    }

    removeGenderFilter(index: number) {
        this.setState(({genders}) => ({ genders: [...genders.slice(0, index), ...genders.slice(index + 1)], filtersModified: true }));
    }
    
    removeYearGradeFilter(index: number) {
        this.setState(({yearGrades}) => ({ yearGrades: [...yearGrades.slice(0, index), ...yearGrades.slice(index + 1)], filtersModified: true }));
    }
    
    removeProgramFilter(index: number) {
        this.setState(({programs}) => ({ programs: [...programs.slice(0, index), ...programs.slice(index + 1)], filtersModified: true }));
    }
    
    removeTeacherFilter(index: number) {
        this.setState(({teachers}) => ({ teachers: [...teachers.slice(0, index), ...teachers.slice(index + 1)], filtersModified: true }));
    }

    async fetchData() {
        this.setState({isLoading: true, filtersModified: false});
        try {
            const { startDate, endDate, courseIds, classIds, schoolIds, genders, yearGrades, programs, teachers } = this.state;

            if(startDate == undefined || endDate == undefined) {
                throw('Datumintervall saknas...');
            }

            let db_data = await DataAccess.Statistics.GetForecastAggregatedMinimal(
                {
                    startDate, endDate, 
                    classIds: classIds.map(e => e.value), 
                    courseNames: courseIds.map(e => e.value), 
                    schoolIds: schoolIds.map(e => e.value),
                    gender: genders.length == 1 ? genders[0].value : undefined,
                    yearGrades: yearGrades.map(y => y.value),
                    teacherIds: teachers.map(t => t.value),
                    programNames: programs.map(p => p.value)
                });
            let transformedData = db_data
                .map(elem => {
                    const total = elem.prognosis_1 + elem.prognosis_2 + elem.prognosis_3 
                                + elem.prognosis_4 + elem.prognosis_5;

                    return {
                        name: elem.courseDisplayName, 
                        total,
                        total_1: elem.prognosis_1 || 0,
                        total_2: elem.prognosis_2 || 0,
                        total_3: elem.prognosis_3 || 0,
                        total_4: elem.prognosis_4 || 0,
                        total_5: elem.prognosis_5 || 0,
                        "1": total > 0 ? (elem.prognosis_1 / total) * 100 : 0, 
                        "2": total > 0 ? (elem.prognosis_2 / total) * 100 : 0, 
                        "3": total > 0 ? (elem.prognosis_3 / total) * 100 : 0, 
                        "4": total > 0 ? (elem.prognosis_4 / total) * 100 : 0, 
                        "5": total > 0 ? (elem.prognosis_5 / total) * 100 : 100
                    }
                }).filter(e => e.total > 0)
    
            this.setState({data: transformedData, error: null, isLoading: false});
        }
        catch (error) {
            this.setState({error, isLoading: false, filtersModified: true});
        }
    }
    
    render() {
        const { 
            error, isLoading, data, startDate, endDate, filtersModified, 
            courseIds, courseOptions,
            classIds, classOptions,
            schoolIds, schoolOptions,
            genders, genderOptions,
            yearGrades, yearGradeOptions,
            programs, programOptions,
            teachers, teacherOptions
        } = this.state;

        const schoolIdArr = schoolIds.map(s => s.value);

        return (
            <div className="render-home">
                <Navigation role={this.props.role}/>
                <div className="junit-container">
                    <ErrorMessage hidden={error === null} style={{marginBottom:'1rem'}}>{error}</ErrorMessage>
                    <div style={{margin: '1rem 0'}}>
                        <div style={{ display: 'inline-block', padding: '0 1rem'}}>
                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Från:</span> 
                            <ReactDayPickerInput 
                                formatDate={(date) => date.toISOString().slice(0, 10)}
                                placeholder={'ÅÅÅÅ-MM-DD'}
                                inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                dayPickerProps={{
                                    locale: 'sv',
                                    months: months_long_se,
                                    weekdaysLong: weekdays_long_se,
                                    weekdaysShort: weekdays_short_se
                                }}
                                onDayChange={(e) => this.setState({startDate: e, filtersModified: true})}
                            />
                        </div>
                        <div style={{ display: 'inline-block', padding: '0 1rem'}}>
                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Till:</span> 
                            <ReactDayPickerInput 
                                formatDate={(date) => date.toISOString().slice(0, 10)}
                                placeholder={'ÅÅÅÅ-MM-DD'}
                                inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                dayPickerProps={{
                                    locale: 'sv',
                                    months: months_long_se,
                                    weekdaysLong: weekdays_long_se,
                                    weekdaysShort: weekdays_short_se
                                }}
                                onDayChange={(e) => this.setState({endDate: e, filtersModified: true})}
                            />
                        </div>
                        <div style={{ display: 'inline-flex', padding: '0 1rem', float: 'right' }}>
                            <Button disabled={!(startDate && endDate && filtersModified)} onClick={this.fetchData}>Hämta data</Button>
                        </div>
                    </div>
                    <div style={{padding: '1rem', borderTop: 'solid 1px lightgrey'}}>
                        <Row>
                            <Col xs={12} sm={12}>
                                <Row>
                                    <Col xs={12} sm={4}>
                                        <Multiselect
                                            value={courseIds}
                                            onChange={(selected: any) => this.setState({courseIds: selected, filtersModified: true})}
                                            options={courseOptions}
                                            //filterOptions={this.selectFilter}
                                            valueRenderer={() => "Filtrera kurser/ämnen"}
                                        />
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <Multiselect
                                            value={classIds}
                                            onChange={(selected: any) => this.setState({classIds: selected, filtersModified: true})}
                                            options={/*schoolIdArr.length > 0 ? classOptions.filter(e => schoolIdArr.indexOf(e.schoolId) != -1) :*/ classOptions}
                                            //filterOptions={this.selectFilter}
                                            valueRenderer={() => "Filtrera klasser"}
                                        />
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <Multiselect
                                            value={schoolIds}
                                            onChange={(selected: any) => this.setState({schoolIds: selected, filtersModified: true})}
                                            options={schoolOptions}
                                            //filterOptions={this.selectFilter}
                                            valueRenderer={() => "Filtrera skolor"}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={12} sm={12}>
                                <Row style={{paddingTop:'1rem'}}>
                                    <Col xs={12} sm={4}>
                                        <Multiselect
                                            value={teachers}
                                            onChange={(selected: any) => this.setState({teachers: selected, filtersModified: true})}
                                            options={teacherOptions}
                                            valueRenderer={() => "Lärare"}
                                        />
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <Multiselect
                                            value={programs}
                                            onChange={(selected: any) => this.setState({programs: selected, filtersModified: true})}
                                            options={programOptions}
                                            valueRenderer={() => "Utbildningsväg"}
                                        />
                                    </Col>
                                    <Col xs={12} sm={2}>
                                        <Multiselect
                                            value={yearGrades}
                                            onChange={(selected: any) => this.setState({yearGrades: selected, filtersModified: true})}
                                            options={yearGradeOptions}
                                            //filterOptions={this.selectFilter}
                                            disableSearch
                                            valueRenderer={() => "Årskurs"}
                                        />
                                    </Col>
                                    <Col xs={12} sm={2}>
                                        <Multiselect
                                            value={genders}
                                            onChange={(selected: any) => this.setState({genders: selected, filtersModified: true})}
                                            options={genderOptions}
                                            //filterOptions={this.selectFilter}
                                            disableSearch
                                            valueRenderer={() => "Kön"}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        
                    </div>
                    <div style={{padding: '0.5rem', borderTop: 'solid 1px lightgrey'}}>
                        { courseIds.length == 0 && classIds.length == 0 && schoolIds.length == 0 && genders.length == 0 
                          && teachers.length == 0 && yearGrades.length == 0 && programs.length == 0 ? 
                            <div style={{color:'grey', padding: '1rem'}}>Inga filter tillagda</div> 
                        : null}
                        {courseIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#755988', color:'white'}} 
                                onHide={() => this.removeCourseIdFilter(index)}
                            />
                        )}
                        {classIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#6176BD', color:'white'}} 
                                onHide={() => this.removeClassIdFilter(index)}
                            />
                        )}
                        {schoolIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#63B995', color:'white'}} 
                                onHide={() => this.removeSchoolIdFilter(index)}
                            />
                        )}
                        {teachers.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#F77F00', color:'white'}} 
                                onHide={() => this.removeTeacherFilter(index)}
                            />
                        )}
                        {programs.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#94A187', color:'white'}} 
                                onHide={() => this.removeProgramFilter(index)}
                            />
                        )}
                        {yearGrades.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#DF9216', color:'white'}} 
                                onHide={() => this.removeYearGradeFilter(index)}
                            />
                        )}
                        {genders.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#DB436B', color:'white'}} 
                                onHide={() => this.removeGenderFilter(index)}
                            />
                        )}
                    </div>
                    <div style={{borderTop: 'solid 1px lightgrey', overflow: 'hidden'}}>
                        {isLoading ? 
                            <div style={{
                                position: 'absolute',
                                width: '100vw',
                                display: 'flex',
                                alignItems: 'center',
                                verticalAlign: 'middle',
                                left: 0,
                                height: '600px',
                                textAlign: 'center',
                                backgroundColor: '#ffffffd2',
                                zIndex: 10
                            }}>
                                <div style={{textAlign: 'center', width: '100%'}}>
                                    <Loading>Laddar...</Loading>
                                </div>
                            </div>
                            : null
                        }
                        <ResponsiveContainer width="100%" height={600}>
                            <BarChart 
                                data={data}
                                margin={{top: 20, right: 30, left: 20, bottom: 5}}
                            >
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis dataKey="name" 
                                    //interval={0} 
                                    //angle={45}
                                    //dx={0}
                                    //dy={0}
                                    //minTickGap={10}
                                    //textAnchor={'start'}
                                    //axisLine={false}
                                />
                                <YAxis domain={[0, 100]} allowDataOverflow={true}/>
                                <Tooltip formatter={(label, e, { dataKey, payload }) => {
                                        if(typeof(label) === 'number')
                                            return `${parseFloat(label.toFixed(1))}% (${payload[`total_${dataKey}`]})`;
                                        else
                                            return label;
                                    }}/>
                                <Legend />
                                { Prognosis.defaultOptions.map(elem => {

                                    return (<Bar dataKey={elem.key} stackId="a" fill={elem.color} name={elem.value}>
                                        <LabelList dataKey={elem.key} position={"inside"} 
                                            content={(props: LabelProps) => {

                                                const { x, y, width, height, value } = 
                                                    { x: 0, y: 0, width: 0, height: 0, value: 0, ...props};

                                                if(width < 10 || value == 0)
                                                    return null;
                                                
                                                const v = parseFloat(value.toString());

                                                return <g>
                                                        <text style={{fontSize: `${Math.min(width / (4), height / (4))}px`}} x={(x + width / 2)} y={y + height / 2} width={width} textAnchor={'middle'} >
                                                            {(v.toFixed(1)+'%')}
                                                        </text>
                                                    </g>;
                                            }}
                                            formatter={(v: any) => 
                                                (v != 0 ? (parseFloat(v.toFixed(1))+'%') : null)} 
                                        />
                                    </Bar>);
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    }
}